import { __assign, __awaiter, __generator, __spreadArrays } from "tslib";
import isEqual from 'lodash.isequal';
import { Fias } from '../Fias';
import { FiasExtraFields, FiasFields, FiasDivision, } from '../types';
import { FiasAddressElement } from './FiasAddressElement';
import { FiasData } from './FiasData';
import { RUSSIA } from '../constants/defaults';
var FiasAddress = /** @class */ (function () {
    function FiasAddress(_a) {
        var _this = this;
        var _b = _a === void 0 ? {} : _a, fields = _b.fields, additionalFields = _b.additionalFields, errors = _b.errors, country = _b.country, foreignAddress = _b.foreignAddress;
        /**
         * @see https://yt.skbkontur.ru/issue/IF-1134
         */
        this.convertToOriginalFields = function () {
            var _a, _b;
            var originalFields = __assign({}, _this.fields);
            if (((_b = (_a = _this.fields[FiasFields.city]) === null || _a === void 0 ? void 0 : _a.fiasData) === null || _b === void 0 ? void 0 : _b.level) === 'District') {
                originalFields[FiasFields.district] = _this.fields[FiasFields.city];
                delete originalFields.city;
            }
            return FiasAddress.createFromAddress(_this, { fields: originalFields });
        };
        /**
         * @see https://yt.skbkontur.ru/issue/IF-165
         */
        this.convertToCorrectFields = function (_a) {
            var division = _a.division;
            var correctFields = __assign({}, _this.fields);
            if (division === FiasDivision.Municipal && _this.fields.district) {
                correctFields[FiasFields.city] = _this.fields[FiasFields.district];
                delete correctFields[FiasFields.district];
            }
            return FiasAddress.createFromAddress(_this, { fields: correctFields });
        };
        this.getAddressErrors = function () {
            return __assign({}, _this.errors);
        };
        this.getText = function (minField, skipTypes, connector) {
            if (skipTypes === void 0) { skipTypes = false; }
            if (connector === void 0) { connector = ', '; }
            if (!_this.hasMainFields) {
                return '';
            }
            var getElementText = function (element) {
                return element ? element.getText(skipTypes) : '';
            };
            var fields = minField ? FiasAddress.getParentFields(minField) : FiasAddress.MAIN_FIELDS;
            return fields
                .map(function (field) { return getElementText(_this.fields[field]); })
                .filter(Boolean)
                .join(connector);
        };
        this.getFullText = function (withPostalCode) {
            if (withPostalCode === void 0) { withPostalCode = false; }
            var substrings = [];
            if (withPostalCode) {
                substrings.push(_this.postalCode);
            }
            if (_this.country && _this.country.fullName) {
                substrings.push(_this.country.fullName);
            }
            substrings.push(_this.isForeign ? _this.foreignAddress : _this.getText());
            return substrings.filter(Boolean).join(', ');
        };
        this.isAllowedToFill = function (field) {
            var _a = _this.fields, region = _a.region, city = _a.city, district = _a.district, settlement = _a.settlement, house = _a.house, garhouse = _a.garhouse, planningstructure = _a.planningstructure;
            var cityLikeFields = [city, district, settlement, planningstructure];
            var houseLikeFields = [house, garhouse];
            switch (field) {
                case FiasFields.street:
                case FiasFields.stead:
                case FiasFields.house:
                case FiasFields.garhouse:
                    return cityLikeFields.some(_this.isFiasData) || !!(region === null || region === void 0 ? void 0 : region.isFederalCity);
                case FiasFields.room:
                    return houseLikeFields.some(_this.isFiasData);
            }
            return true;
        };
        this.isFiasData = function (el) { var _a; return !!((_a = el === null || el === void 0 ? void 0 : el.data) === null || _a === void 0 ? void 0 : _a.fiasId); };
        this.isAllowedToSearchFullAddress = function (field) {
            return FiasAddress.FULL_ADDRESS_SEARCH_FIELDS.indexOf(field) > -1;
        };
        this.isAllowedToSearchFieldOnlyInClosestFilledParent = function (field, fieldsSettings, _a) {
            var division = _a.division, schema = _a.schema;
            if (fieldsSettings) {
                // if there are parent fields that have been hidden by user
                // then we have to prevent them from getting into address
                // because it will not pass the verification (now)
                // see https://github.com/skbkontur/retail-ui/issues/1575
                for (var _i = 0, _b = FiasAddress.getParentFields(field); _i < _b.length; _i++) {
                    var parentField = _b[_i];
                    var isParentFieldVisible = FiasAddress.isFieldVisible(parentField, fieldsSettings);
                    var isParentFieldVisibleByDefault = Fias.getDefaultFieldVisibility(parentField, division, schema);
                    if (!isParentFieldVisible && isParentFieldVisibleByDefault) {
                        return true;
                    }
                }
            }
            return FiasAddress.NOT_ONLY_DIRECT_PARENT_SEARCH_FIELDS.indexOf(field) === -1;
        };
        this.hasOnlyIndirectParent = function (field) {
            if (field) {
                var parents = FiasAddress.getParentFields(field);
                if (parents.length > 1) {
                    var directParent = _this.fields[parents.pop()];
                    return !directParent && parents.some(function (parent) { return Boolean(_this.fields[parent]); });
                }
            }
            return false;
        };
        this.getClosestParentFiasId = function (field) {
            if (_this.hasMainFields) {
                var parents = FiasAddress.getParentFields(field).slice().reverse();
                for (var _i = 0, parents_1 = parents; _i < parents_1.length; _i++) {
                    var parentField = parents_1[_i];
                    var parent_1 = _this.fields[parentField];
                    if (parent_1) {
                        return parent_1.fiasId;
                    }
                }
            }
        };
        this.getFiasId = function () {
            if (_this.hasMainFields) {
                var fields = FiasAddress.MAIN_FIELDS.slice().reverse();
                for (var _i = 0, fields_1 = fields; _i < fields_1.length; _i++) {
                    var field = fields_1[_i];
                    var element = _this.fields[field];
                    if (element && element.fiasId) {
                        return element.fiasId;
                    }
                }
            }
            return '';
        };
        this.getParent = function (field) {
            if (_this.fields[field]) {
                var parents = FiasAddress.getParentFields(field).filter(function (f) { return Boolean(_this.fields[f]); });
                var closest = parents.pop();
                if (closest) {
                    return _this.fields[closest];
                }
            }
        };
        this.getFiasPostalCode = function () {
            if (_this.hasMainFields) {
                var fields = FiasAddress.MAIN_FIELDS.slice().reverse();
                for (var _i = 0, fields_2 = fields; _i < fields_2.length; _i++) {
                    var field = fields_2[_i];
                    var element = _this.fields[field];
                    if (element && element.data && element.data.postalCode) {
                        return element.data.postalCode;
                    }
                }
            }
            return '';
        };
        this.getAddressValue = function () {
            var fields = Object.keys(_this.fields);
            return fields.reduce(function (value, field) {
                var _a;
                var element = _this.fields[field];
                if (!element) {
                    return value;
                }
                var name = element.name, fiasData = element.fiasData;
                return __assign(__assign({}, value), (_a = {}, _a[field] = __assign({ name: name, text: element.getText() }, (fiasData ? { data: fiasData } : {})), _a));
            }, {});
        };
        // TODO: get fields usage from fieldsSettings
        this.getValue = function (withPostalCode, division) {
            if (withPostalCode === void 0) { withPostalCode = false; }
            var _a = _this, country = _a.country, foreignAddress = _a.foreignAddress;
            return __assign(__assign(__assign({ address: _this.getAddressValue(), addressWithOriginalFields: _this.convertToOriginalFields().getAddressValue(), addressString: _this.getFullText(withPostalCode), addressErrors: _this.getAddressErrors(), fiasId: _this.getFiasId(), postalCode: _this.postalCode }, (country ? { country: country } : {})), (_this.isForeign ? { foreignAddress: foreignAddress } : {})), { division: division });
        };
        this.convertForVerification = function () {
            return FiasAddress.MAIN_FIELDS.reduce(function (value, field) {
                var _a;
                var element = _this.fields[field];
                if (!element) {
                    return value;
                }
                return __assign(__assign({}, value), (_a = {}, _a[field] = __assign({}, element.verifiableData), _a));
            }, {});
        };
        this.getDiffFields = function (address, fieldsSettings) {
            var fields = fieldsSettings
                ? FiasAddress.filterVisibleFields(address.fields, fieldsSettings)
                : __assign({}, address.fields);
            for (var _i = 0, _a = FiasAddress.MAIN_FIELDS; _i < _a.length; _i++) {
                var field = _a[_i];
                var element = fields[field];
                if (element) {
                    if (element.isEqualTo(_this.fields[field])) {
                        delete fields[field];
                    }
                    else {
                        break;
                    }
                }
            }
            return fields;
        };
        this.fields = fields || {};
        this.additionalFields = additionalFields || {};
        this.errors = errors || {};
        this.country = country;
        this.foreignAddress = foreignAddress || '';
    }
    Object.defineProperty(FiasAddress.prototype, "isForeign", {
        get: function () {
            return Boolean(this.country && !FiasAddress.IS_RUSSIA(this.country));
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FiasAddress.prototype, "hasMainFields", {
        get: function () {
            var _this = this;
            return FiasAddress.MAIN_FIELDS.some(function (field) { return Object.prototype.hasOwnProperty.call(_this.fields, field); });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FiasAddress.prototype, "isEmpty", {
        get: function () {
            return this.isForeign ? !this.foreignAddress : !this.hasMainFields;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FiasAddress.prototype, "hasErrors", {
        get: function () {
            return Object.keys(this.errors).length > 0;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FiasAddress.prototype, "postalCode", {
        get: function () {
            var value = this.additionalFields[FiasExtraFields.postalcode];
            return typeof value === 'string' ? value : this.getFiasPostalCode();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FiasAddress.prototype, "isPostalCodeValid", {
        get: function () {
            var rusFormat = /^[\d]{6}$/;
            var foreignFormat = /^[\w.\-\s]*$/;
            return (!this.country || this.isForeign ? foreignFormat : rusFormat).test(this.postalCode);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FiasAddress.prototype, "isPostalCodeAltered", {
        get: function () {
            var fiasPostalCode = this.getFiasPostalCode();
            return Boolean(fiasPostalCode) && this.postalCode !== fiasPostalCode;
        },
        enumerable: false,
        configurable: true
    });
    FiasAddress.prototype.hasError = function (field) {
        return Object.prototype.hasOwnProperty.call(this.errors, field);
    };
    FiasAddress.prototype.getError = function (field) {
        return this.errors[field];
    };
    FiasAddress.MAIN_FIELDS = [
        FiasFields.region,
        FiasFields.municipaldistrict,
        FiasFields.district,
        FiasFields.urbansettlement,
        FiasFields.city,
        FiasFields.intracityarea,
        FiasFields.settlement,
        FiasFields.planningstructure,
        FiasFields.street,
        FiasFields.stead,
        FiasFields.garhouse,
        FiasFields.house,
        FiasFields.room,
    ];
    FiasAddress.ADDITIONAL_FIELDS = [FiasExtraFields.postalcode];
    FiasAddress.ALL_FIELDS = __spreadArrays(FiasAddress.MAIN_FIELDS, FiasAddress.ADDITIONAL_FIELDS);
    FiasAddress.FULL_ADDRESS_SEARCH_FIELDS = [
        FiasFields.municipaldistrict,
        FiasFields.district,
        FiasFields.urbansettlement,
        FiasFields.city,
        FiasFields.intracityarea,
        FiasFields.settlement,
        FiasFields.planningstructure,
        FiasFields.street,
    ];
    FiasAddress.NOT_ONLY_DIRECT_PARENT_SEARCH_FIELDS = [
        FiasFields.municipaldistrict,
        FiasFields.district,
        FiasFields.urbansettlement,
        FiasFields.city,
        FiasFields.intracityarea,
        FiasFields.settlement,
        FiasFields.planningstructure,
        FiasFields.street,
    ];
    FiasAddress.IS_RUSSIA = function (country) {
        return isEqual(country, RUSSIA);
    };
    FiasAddress.isCountryFull = function (country) {
        return Boolean(country.fullName && country.shortName);
    };
    FiasAddress.responseToFields = function (response) {
        var fields = {};
        if (response) {
            FiasAddress.MAIN_FIELDS.forEach(function (field) {
                var fiasObject = response[field];
                if (fiasObject) {
                    var data = new FiasData(fiasObject);
                    var name_1 = data.name;
                    fields[field] = new FiasAddressElement(field, name_1, data);
                }
            });
        }
        return fields;
    };
    FiasAddress.fieldsToResponse = function (fields) {
        var response = {};
        if (fields) {
            FiasAddress.MAIN_FIELDS.forEach(function (field) {
                var element = fields[field];
                if (element && element.fiasData) {
                    response[field] = element.fiasData;
                }
            });
        }
        return response;
    };
    FiasAddress.createFromResponse = function (response, additionalFields, country) {
        return new FiasAddress({
            fields: FiasAddress.responseToFields(response),
            additionalFields: additionalFields,
            country: country,
        });
    };
    FiasAddress.createFromAddressValue = function (addressValue, additionalFields, country) {
        var fields = {};
        if (addressValue) {
            FiasAddress.MAIN_FIELDS.forEach(function (field) {
                var addressField = addressValue[field];
                if (addressField) {
                    var name_2 = addressField.name, data = addressField.data;
                    fields[field] = new FiasAddressElement(field, name_2, data && new FiasData(data));
                }
            });
        }
        return new FiasAddress({ fields: fields, additionalFields: additionalFields, country: country });
    };
    FiasAddress.createFromAddress = function (address, options) {
        var fields = address.fields, additionalFields = address.additionalFields, errors = address.errors, country = address.country, foreignAddress = address.foreignAddress;
        return new FiasAddress(__assign({ fields: fields,
            additionalFields: additionalFields,
            errors: errors,
            country: country,
            foreignAddress: foreignAddress }, options));
    };
    FiasAddress.validate = function (address, locale, fieldsSettings) {
        var fields = address.fields;
        var errors = {};
        var _loop_1 = function (field) {
            var element = fields[field];
            var error = FiasAddress.validateField(field, element, fieldsSettings, function () {
                if (element) {
                    if (!element.data) {
                        return locale.addressNotVerified;
                    }
                    if (!address.isAllowedToFill(field)) {
                        return locale[field + "FillBefore"];
                    }
                    if (element.isUnknown) {
                        return locale.addressUnknown;
                    }
                }
            });
            if (error) {
                errors[field] = error;
            }
        };
        for (var _i = 0, _a = FiasAddress.MAIN_FIELDS; _i < _a.length; _i++) {
            var field = _a[_i];
            _loop_1(field);
        }
        if (Object.keys(errors).length === 0) {
            var field = FiasExtraFields.postalcode;
            var postalCode_1 = address.postalCode;
            var error = FiasAddress.validateField(field, postalCode_1, fieldsSettings, function () {
                if (postalCode_1 && !address.isPostalCodeValid) {
                    return locale.postalcodeNotValid;
                }
                else if (address.isPostalCodeAltered) {
                    return locale.postalcodeNotFound;
                }
            });
            if (error) {
                errors[field] = error;
            }
        }
        return FiasAddress.createFromAddress(address, { errors: errors });
    };
    FiasAddress.validateField = function (field, value, fieldsSettings, fiasValidation) {
        var _a;
        var customValidation = (_a = fieldsSettings[field]) === null || _a === void 0 ? void 0 : _a.validation;
        if (customValidation === false) {
            return '';
        }
        var error = fiasValidation() || '';
        if (typeof customValidation === 'function') {
            error = customValidation(value, field, error);
        }
        return error;
    };
    FiasAddress.verify = function (address, response) {
        if (!address.hasMainFields) {
            return address;
        }
        var addressResponse = response.address, invalidLevel = response.invalidLevel;
        var verifiedAddress = FiasAddress.createFromAddress(address, {
            fields: __assign(__assign({}, address.fields), FiasAddress.responseToFields(addressResponse)),
        });
        if (invalidLevel) {
            var invalidFields = __spreadArrays([invalidLevel], FiasAddress.getChildFields(invalidLevel)).filter(function (field) { var _a; return !((_a = address.fields[field]) === null || _a === void 0 ? void 0 : _a.isUnknown); });
            return FiasAddress.removeFiasData(verifiedAddress, invalidFields);
        }
        return verifiedAddress;
    };
    // TODO: Hide invisible fields without removing them.
    // (removing can break the verification)
    FiasAddress.filterVisibleFields = function (fields, fieldsSettings) {
        var filteredFields = {};
        var field;
        for (field in fields) {
            if (FiasAddress.isFieldVisible(field, fieldsSettings)) {
                filteredFields[field] = fields[field];
            }
        }
        return filteredFields;
    };
    FiasAddress.isFieldVisible = function (field, fieldsSettings) {
        var settings = fieldsSettings[field];
        return Boolean(settings && settings.visible);
    };
    FiasAddress.getParentFields = function (field) {
        var index = FiasAddress.MAIN_FIELDS.indexOf(field);
        return index > -1 ? FiasAddress.MAIN_FIELDS.slice(0, index) : [];
    };
    FiasAddress.getChildFields = function (field) {
        var index = FiasAddress.MAIN_FIELDS.indexOf(field);
        return index > -1 ? FiasAddress.MAIN_FIELDS.slice(index + 1) : [];
    };
    FiasAddress.removeFiasData = function (address, fields) {
        if (fields === void 0) { fields = FiasAddress.MAIN_FIELDS; }
        var addressFields = __assign({}, address.fields);
        for (var _i = 0, fields_3 = fields; _i < fields_3.length; _i++) {
            var field = fields_3[_i];
            var element = addressFields[field];
            if (element) {
                element.removeData();
            }
        }
        return FiasAddress.createFromAddress(address, { fields: addressFields });
    };
    FiasAddress.getAddress = function (api, value, fieldsSettings) { return __awaiter(void 0, void 0, void 0, function () {
        var addressValue, addressString, fiasId, postalCode, foreignAddress, country, additionalFields, searchOptions, _a, success, data, division, address, correctFields, visibleFields, _b, success, data, addressResponse;
        var _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    if (!value) {
                        return [2 /*return*/, new FiasAddress()];
                    }
                    addressValue = value.address, addressString = value.addressString, fiasId = value.fiasId, postalCode = value.postalCode, foreignAddress = value.foreignAddress;
                    country = value.country;
                    additionalFields = {};
                    searchOptions = {};
                    if (postalCode) {
                        additionalFields[FiasExtraFields.postalcode] = postalCode;
                    }
                    if (!country) return [3 /*break*/, 3];
                    if (!!FiasAddress.isCountryFull(country)) return [3 /*break*/, 2];
                    return [4 /*yield*/, api.searchCountry({ prefix: country.code })];
                case 1:
                    _a = _d.sent(), success = _a.success, data = _a.data;
                    if (success && data) {
                        country = data[0];
                    }
                    _d.label = 2;
                case 2:
                    if (!FiasAddress.IS_RUSSIA(country)) {
                        return [2 /*return*/, new FiasAddress({
                                country: country,
                                foreignAddress: foreignAddress,
                                additionalFields: (_c = {}, _c[FiasExtraFields.postalcode] = postalCode, _c),
                            })];
                    }
                    _d.label = 3;
                case 3:
                    if (addressValue) {
                        division = api.division;
                        address = FiasAddress.createFromAddressValue(addressValue, additionalFields, country);
                        correctFields = address.convertToCorrectFields({ division: division }).fields;
                        visibleFields = fieldsSettings
                            ? FiasAddress.filterVisibleFields(correctFields, fieldsSettings)
                            : correctFields;
                        return [2 /*return*/, FiasAddress.createFromAddress(address, { fields: visibleFields })];
                    }
                    if (fiasId) {
                        searchOptions = {
                            fiasId: fiasId,
                        };
                    }
                    if (addressString) {
                        searchOptions = {
                            searchText: addressString,
                            limit: 1,
                        };
                    }
                    if (!api) return [3 /*break*/, 5];
                    return [4 /*yield*/, api.search(searchOptions)];
                case 4:
                    _b = _d.sent(), success = _b.success, data = _b.data;
                    if (success && data && data.length) {
                        addressResponse = fieldsSettings ? FiasAddress.filterVisibleFields(data[0], fieldsSettings) : data[0];
                        return [2 /*return*/, FiasAddress.createFromResponse(addressResponse, additionalFields, country)];
                    }
                    _d.label = 5;
                case 5: return [2 /*return*/, new FiasAddress({ country: country })];
            }
        });
    }); };
    return FiasAddress;
}());
export { FiasAddress };
